<template>
<div class="section">
  <div class="container">
    <div v-if="profile">
      <profile-header :user="user" :profile="profile"></profile-header>
      <tab-selector :user="user" :profile="profile"></tab-selector>
      <component :is="currentTab" :user="user" :profile="profile" class="fit-component"></component>
      <scroll-to-top></scroll-to-top>
    </div>
    <div v-if="notFound" class="not-found fit-component title is-6">
      <span class="icon"><i class="fal fa-user-secret" aria-hidden="true"></i></span>
      &nbsp;&nbsp;
      Uh oh, we can't seem to find that user
    </div>
  </div>
  <modal-custom v-if="showDiscardChangesModal"
    :hasFooter="true" :title="'Discard unsaved changes'" :text="'Navigating away will discard your unsaved changes. Are you sure you want to continue?'"
    :successText="'Discard'" :cancelText="'Cancel'" @modalSuccess="handleDiscard()"
    @modalCancel="showDiscardChangesModal = false">
  </modal-custom>
  <modal-custom v-if="statusChangedSuccess"
                :hasFooter="true" :text="statusChangedText"
                :successText="'Ok'" @modalSuccess="closeStudentModal()">
  </modal-custom>
</div>
</template>

<script>
import auth from '@/mixins/auth'
import limitedLogin from '@/mixins/limited-login'
import linkedIn from './linkedin'

import socket from '@/libs/socket'

import ProfileHeader from './Header'
import TabSelector from './TabSelector'
import ScrollToTop from '@/components/Controls/ScrollTopButton'

import TabAbout from './tabs/About/'
import TabProjectBoards from './tabs/Projects'
import TabStoryBoards from './tabs/Stories'
import TabContributions from './tabs/Contributions'
import TabPins from './tabs/Pins'
import TabFollowing from './tabs/Following'
import TabFollowers from './tabs/Followers'
import TabMyClasses from './tabs/MyClasses'
import TabPersonalInformation from './tabs/PersonalInformation'
import TabSettings from './tabs/Settings'
import PageNotFound from '@/components/Pages/NotFound'
import ModalCustom from '@/components/Modals/Custom'

import componentState from '@/mixins/component-state'

import state from './state'

const NOT_LOGGED_IN = 'Not logged in'

export default {
  components: {
    ProfileHeader,
    TabSelector,
    TabAbout,
    TabProjectBoards,
    TabStoryBoards,
    TabContributions,
    TabPins,
    TabFollowing,
    TabFollowers,
    TabMyClasses,
    TabPersonalInformation,
    TabSettings,
    ScrollToTop,
    PageNotFound,
    ModalCustom
  },
  mixins: [auth, limitedLogin, componentState, linkedIn],
  metaInfo: function () {
    if (this.$isHeise) {
      return {
        title: 'Profil'
      }
    }
    return {
      title: this.title !== '' ? this.$isStories ? this.title : `${this.title}` : ' '
    }
  },
  data: function () {
    return {
      user: {},
      notFound: false,
      loading: false,
      title: 'Profile',
      showDiscardChangesModal: false,
      discardPath: null,
      statusChangedSuccess: false,
      changedStudentStatus: '',
      showManageClassTab: false
    }
  },
  computed: {
    currentTab: function () {
      let defaultTab = state.tab.defaultProjects
      if (this.$isEngCom) {
        if (!this.user.isEngStaff) {
          defaultTab = state.tab.defaultCantPublish
        } else {
          defaultTab = state.tab.defaultStories
        }
      }
      if ((this.$isYSC || this.$isOhioScience) && this.showManageClassTab) {
        defaultTab = state.tab.manageClassTab
      }
      return this._get(state.tab.current, defaultTab)
    },
    profile: function () {
      return this.user.userName && this.$store.state.user.profiles[this.user.userName] ? this.$store.state.user.profiles[this.user.userName] : null
    },
    studentStatus: function () {
      return this.$route.query.studentStatus
    },
    studentUsername: function () {
      return this.$route.query.userName
    },
    statusChangedText: function () {
      return 'Student has been removed successfully.'
    }
  },
  watch: {
    '$route.params.username': function () {
      this.fetch()
    },
    isLoggedIn: function (loggedIn) {
      if (loggedIn) {
        this.fetch()
      } else if (!loggedIn && this.$route.path === '/profile') {
        this.user = ''
        this.$router.push({ name: 'home' })
      }
    }
  },
  mounted: function () {
    if (this.$route.query.activeTab === 'TabMyClasses') {
      this.showManageClassTab = true
    }
    if (this.$route.query.studStatus === 'update') {
      this.$store.dispatch('updateStudentStatus', { studentStatus: this.studentStatus, studentUsername: this.studentUsername }).then((status) => {
        if (status) {
          this.changedStudentStatus = status
          this.statusChangedSuccess = true
        }
      })
    }
    this.fetch()
    this.initCallbacks()

    if (this.isLinkedInRedirect) {
      this.handleLinkedInRedirect()
    }
  },
  beforeRouteLeave: function (to, from, next) {
    if (this._get(state.editingCount) > 0) {
      this.discardPath = to
      this.showDiscardChangesModal = true
    } else {
      next()
    }
  },
  methods: {
    initCallbacks: function () {
      this._set(state.editingCount, 0)
      this._register(state.callbacks.save.profile, this.saveProfile)
      this._register(state.callbacks.save.skills, this.saveSkills)
      this._register(state.callbacks.save.interests, this.saveInterests)
      this._register(state.callbacks.save.user, this.saveUser)
      this._register(state.callbacks.save.settings, this.saveSettings)
      this._register(state.callbacks.linkedIn.auth, this.linkedInAuth)
    },
    fetch: function () {
      this.loading = true
      this.getUser().then(() => {
        this.getProfile()
        this.loading = false
      }).catch(() => {
        this.loading = false
        this.notFound = true
      })
    },
    getUser: function () {
      return new Promise((resolve, reject) => {
        const userName = this.$route.params.username

        const previousUUID = this._get(state.uuid)
        const previousTab = this._get(state.tab.current)

        this._clear()

        if (this.token) {
          this.limitedLogin().then((user) => {
            this.setUser(user, false, true, previousUUID, previousTab)
            this._set(state.tab.current, 'TabSettings')
            resolve()
          }, (messages) => {
            reject(messages)
          })
        } else if (userName && userName !== this.currentUser.userName) {
          this.$store.dispatch('getUser', userName).then((user) => {
            this.setUser(user, false, false, previousUUID, previousTab)
            socket.view('profile', 0, user.uuid)
            resolve()
          }, (messages) => {
            reject(messages)
          })
        } else if (this.hasCurrentUser) {
          this.setUser(this.currentUser, true, true, previousUUID, previousTab)
          resolve()
        } else {
          reject(NOT_LOGGED_IN)
        }
      })
    },
    setUser: function (user, editProfile, editSettings, previousUUID, previousTab) {
      this.user = user
      this._set(state.uuid, user.uuid)
      this._set(state.editable.profile, editProfile)
      this._set(state.editable.settings, editSettings)

      if (user.uuid === previousUUID) {
        this._set(state.tab.current, previousTab)
      }
    },
    getProfile: function () {
      this.$store.dispatch('getProfile', this.user.userName).then((profile) => {
        this.getProfileCounts()
      }).catch((messages) => {
        // @todo handle the error case
      })
    },
    getProfileCounts: function () {
      this.$store.dispatch('getProfileCounts', { username: this.user.userName })
    },
    saveUser: function (data) {
      return this.$store.dispatch('updateCredentials', { username: this.user.userName, data }).then((user) => {
        this.user = user
      })
    },
    saveProfile: function (data) {
      const profile = {}
      profile.location = data.location !== null && data.location !== undefined ? data.location : this.profile.location
      profile.bio = data.bio !== null && data.bio !== undefined ? data.bio : this.profile.bio
      profile.intro = data.intro !== null && data.intro !== undefined ? data.intro : this.profile.intro
      profile.engineeringType = data.engineeringType !== null && data.engineeringType !== undefined ? data.engineeringType : this.profile.engineeringType
      profile.mentorDescription = data.mentorDescription !== null && data.mentorDescription !== undefined ? data.mentorDescription : this.profile.mentorDescription
      profile.isLinkedIn = data.isLinkedIn !== null && data.isLinkedIn !== undefined ? data.isLinkedIn : this.profile.isLinkedIn
      profile.areaOfInterest = data.areaOfInterest !== null && data.areaOfInterest !== undefined ? data.areaOfInterest : this.profile.areaOfInterest

      return this.$store.dispatch('updateProfile', { username: this.user.userName, profile })
    },
    saveSkills: function (skills) {
      return this.$store.dispatch('setSkills', { username: this.user.userName, skills })
    },
    saveInterests: function (interests) {
      return this.$store.dispatch('setInterests', { username: this.user.userName, interests })
    },
    saveSettings: function (settings) {
      return this.$store.dispatch('updateUserSettings', { username: this.user.userName, settings })
    },
    handleDiscard: function () {
      this._set(state.editingCount, 0)
      this.$router.push(this.discardPath)
    },
    closeStudentModal: function () {
      this.statusChangedSuccess = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

.section {
  background-color: #F2F2F2;
  @include mobile {
    padding: 1rem;
  }
}

.fit-component{
  min-height: calc(100vh - #{$navbar-height});
}

.not-found {
  text-align: center;
}
</style>
